import { Badge, Box, Chip, IconButton, Stack, Theme, Tooltip, Typography } from '@mui/material';
import { LoadingPlace, LoadingPlaceTypes } from '../models/booking';
import { OfferStatus, PotentialOffer, Tenant, timocomPluginData } from '../types';
import React, { ReactElement } from 'react';
import { t } from 'i18next';
import { getDisplayPrice } from '../pages/loadSearch/components/priceUtils';
import { ChatBubbleOutline, Circle, EmailOutlined, LockClock, Mail, PsychologyAlt, WarningAmberOutlined } from '@mui/icons-material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { OfferScoreComponents } from '../models/offer';
import { MarketplaceChip } from '../components/marketplaceChip';


export class OfferRenderer {

  private offer: Partial<PotentialOffer>;
  private loadingPlaces: LoadingPlace[];
  private deliveryPlaces: LoadingPlace[];
  private isNegotiatedElsewhere: boolean;
  private theme: Theme;
  private tenant: Tenant | null;

  constructor(offer: Partial<PotentialOffer>, theme: Theme, tenant: Tenant | null = null) {
    this.offer = offer;
    this.loadingPlaces = this.offer.loadingPlaces?.filter((place: LoadingPlace) => place.loadingType === LoadingPlaceTypes.LOADING) || [];
    this.deliveryPlaces = this.offer.loadingPlaces?.filter((place: LoadingPlace) => place.loadingType === LoadingPlaceTypes.UNLOADING) || [];
    this.isNegotiatedElsewhere = !!this.offer.otherNegotiatingVehicle;
    this.theme = theme;
    this.tenant = tenant;
  }

  private static formatDateShortJSX(date: string | number | Date | undefined) {
    if (!date) return t('N/A');
    const d = new Date(date);
    const parts = new Intl.DateTimeFormat(undefined, {
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);

    return parts.map((part, index) => {
      if (part.type === 'day') {
        return (
          <strong key={index} style={{ fontWeight: 600 }}>
            {part.value}
          </strong>
        );
      }
      return <span key={index}>{part.value}</span>;
    });
  }

  // Helper function to format time as HH:MM
  private static formatTime(value: string | undefined): string {
    if (!value) return '';
    // If value is already in HH:MM format, return it
    if (/^\d{2}:\d{2}$/.test(value)) {
      return value;
    }
    // If value is a datetime string, extract time
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '';
    }
  }

  // Helper function to calculate days difference between two dates
  private static calculateDaysDifference(earliestDate: string | number | Date | undefined, latestDate: string | number | Date | undefined): number {
    if (!earliestDate || !latestDate) return 0;
    try {
      const earliest = new Date(earliestDate);
      const latest = new Date(latestDate);
      if (isNaN(earliest.getTime()) || isNaN(latest.getTime())) return 0;
      const diffTime = latest.getTime() - earliest.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    } catch {
      return 0;
    }
  }

  private static InfoTooltip({
    offer,
    children
  }: {
    offer: Partial<PotentialOffer>;
    children: ReactElement<unknown, any>;
  }) {
    const freightDescription = `${t('OFFER DETAILS')}:
                    ${t('Freight description')}: ${offer.freightDescription || t('N/A')}\n`;

    const acceptQuotes = `${t('Accepts quotes')}: ${timocomPluginData(offer)?.acceptQuotes ? t('Yes') : t('No')}\n`;

    const contactPerson = offer.contactPerson;
    const contactInfo = `
                      ${t('CONTACT')}:
                      ${t('Name')}: ${contactPerson?.firstName || ''} ${contactPerson?.lastName || ''}
                      ${t('Email')}: ${contactPerson?.email || t('N/A')}
                      ${t('Phone')}: ${contactPerson?.phone || t('N/A')}
                      `;

    const vehicleProperties = offer.vehicleProperties;
    const toList = (val: any) => Array.isArray(val) ? val.join(', ') : val || t('N/A');
    const vehicleInfo = `${t('VEHICLE')}:
                      ${t('Type')}: ${toList(vehicleProperties?.type)}
                      ${t('Body')}: ${toList(vehicleProperties?.body)}
                      ${t('Body Property')}: ${toList(vehicleProperties?.bodyProperty)}
                      ${t('Equipment')}: ${toList(vehicleProperties?.equipment)}
                      ${t('Load Securing')}: ${toList(vehicleProperties?.loadSecuring)}
                      ${t('Swap Body')}: ${toList(vehicleProperties?.swapBody)}
                      `;

    const paymentDueWithinDays = `${t('Payment due within days')}: ${offer.paymentDueWithinDays || 0}`;

    return <Tooltip title={
      <Stack spacing={2} direction="row">
        <Box>
          {freightDescription}
          {acceptQuotes}
          {contactInfo}
        </Box>
        <Box>
          {vehicleInfo}
          {paymentDueWithinDays}
        </Box>
      </Stack>
    } arrow placement="top" slotProps={{ tooltip: { sx: { maxWidth: 400, fontSize: '0.875rem', whiteSpace: 'pre-line' } } }} >
      {children}
    </Tooltip>;
  }

  // Helper function to get color based on score (0-100)
  private static getScoreColor(score: number): string {
    if (score >= 80) {
      // 80-100: Green scale (higher = stronger green)
      // At 80: lighter green (#81c784), at 100: strong green (#2e7d32)
      const ratio = (score - 80) / 20; // 0 at 80, 1 at 100
      const red = Math.round(46 + (129 - 46) * (1 - ratio));
      const green = Math.round(125 + (201 - 125) * (1 - ratio));
      const blue = Math.round(132 + (196 - 132) * (1 - ratio));
      return `rgb(${red}, ${green}, ${blue})`;
    } else if (score >= 50) {
      // 50-80: Yellow to green transition
      // At 50: yellow (#ffc107), at 80: lighter green (#81c784)
      const ratio = (score - 50) / 30; // 0 at 50, 1 at 80
      const red = Math.round(255 + (129 - 255) * ratio);
      const green = Math.round(193 + (201 - 193) * ratio);
      const blue = Math.round(7 + (132 - 7) * ratio);
      return `rgb(${red}, ${green}, ${blue})`;
    } else {
      // 0-50: Red to yellow transition
      // At 0: red (#f44336), at 50: yellow (#ffc107)
      const ratio = score / 50; // 0 at 0, 1 at 50
      const red = Math.round(244 + (255 - 244) * ratio);
      const green = Math.round(67 + (193 - 67) * ratio);
      const blue = Math.round(54 + (7 - 54) * ratio);
      return `rgb(${red}, ${green}, ${blue})`;
    }
  };

  /**
   * User-friendly descriptions for each offer status.
   * These are displayed as tooltips to help users understand what each status means.
   */
  private static offerStatusDescriptions: Record<OfferStatus, string> = {
    [OfferStatus.WITHOUT_CONTACT]: t('No contact information available for this client'),
    [OfferStatus.WITH_CONTACT]: t('Contact information obtained; ready for outreach'),
    [OfferStatus.NEGOTIATION_STARTED]: t('Initial negotiation message sent to client'),
    [OfferStatus.NEGOTIATION_PRICE_RECEIVED]: t('Client has submitted their price proposal'),
    [OfferStatus.NEGOTIATION_PRICE_OFFERED]: t('Our price proposal has been sent to client'),
    [OfferStatus.NEGOTIATION_ACCEPTED]: t('Acceptance message sent to client; awaiting order confirmation'),
    [OfferStatus.NEGOTIATION_DECLINED]: t('Offer has been declined'),
    [OfferStatus.NEGOTIATION_NEED_HUMAN]: t('Requires human review - cannot proceed autonomously'),
    [OfferStatus.NEGOTIATION_WAITING_FINAL_CONFIRM]: t('Awaiting management approval before sending final confirmation mail to client'),
    [OfferStatus.NEGOTIATION_MANUALLY_CLOSED]: t('Negotiation closed with notification sent to client'),
    [OfferStatus.NEGOTIATION_BACK_CONFIRMED]: t('Order confirmed and booking created successfully'),
    [OfferStatus.NEGOTIATION_MANUALLY_CLOSED_UNDISCLOSED]: t('Negotiation closed without notification to client'),
  };

  /**
   * Get the user-friendly description for an offer status
   */
  private static getOfferStatusDescription(status: OfferStatus): string {
    return OfferRenderer.offerStatusDescriptions[status] || t('Unknown status');
  };

  private static needAction(offer: Partial<PotentialOffer>) {
    return offer?.negotiations && offer?.negotiations[0]?.hasUnsent;
  }

  renderId() {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'center', fontSize: (theme: any) => theme.typography.table.body }}>
          {this.offer.id}
        </Typography>
        <MarketplaceChip externalPlatform={this.offer.externalPlatform} size="xs" />
        {this.isNegotiatedElsewhere && (
          <Chip
            label={t('Other')}
            size="small"
            sx={{
              fontSize: '0.6rem',
              height: 16,
              backgroundColor: 'warning.light',
              color: 'warning.dark',
              '& .MuiChip-label': { px: 0.5 }
            }}
          />
        )}
      </Box>
    );
  }

  renderRoute() {
    const formatAllPlacesForTooltip = () => {
      const loadingList = this.loadingPlaces.map((place, index) => {
        const postalCodePrefix = place.address.postalCode?.substring(0, 2) || '';
        return `${index + 1}. ${place.address.country}-${postalCodePrefix} ${place.address.city}`;
      }).join('\n');

      const deliveryList = this.deliveryPlaces.map((place, index) => {
        const postalCodePrefix = place.address.postalCode?.substring(0, 2) || '';
        return `${index + 1}. ${place.address.country}-${postalCodePrefix} ${place.address.city}`;
      }).join('\n');

      return `${t('Loading Places')} (${this.loadingPlaces.length}):\n${loadingList || t('N/A')}\n` +
        '\n' +
        `${t('Delivery Places')} (${this.deliveryPlaces.length}):\n${deliveryList || t('N/A')}`;
    };
    const firstLoadingPlace = this.loadingPlaces[0];
    const lastDeliveryPlace = this.deliveryPlaces[this.deliveryPlaces.length - 1];
    return (
      <Tooltip
        title={
          <Box sx={{ whiteSpace: 'pre-line', fontSize: (theme: any) => theme.typography.table.body }}>
            {formatAllPlacesForTooltip()}
          </Box>
        }
        arrow
        placement="top"
      >
        <Box sx={{ cursor: 'help', textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.body }} fontWeight={600}>
              {firstLoadingPlace.address.country}-{firstLoadingPlace.address.postalCode?.substring(0, 2)} ➜ {lastDeliveryPlace?.address.country}-{lastDeliveryPlace?.address.postalCode?.substring(0, 2)}
            </Typography>
            {this.loadingPlaces.length > 2 && (
              <Chip
                label={`+ ${this.loadingPlaces?.length - 2 || 0}`}
                size="small"
                sx={{
                  fontSize: '0.6rem',
                  height: 16,
                  minWidth: 'auto',
                  backgroundColor: (this.loadingPlaces?.length || 0) > 2 ? 'warning.light' : 'info.light',
                  color: 'black',
                  '& .MuiChip-label': { px: 0.7 }
                }}
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.small, color: 'text.grey' }}>
            {firstLoadingPlace.address.city} ➜ {lastDeliveryPlace?.address.city}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  renderTimeline() {
    const firstLoadingPlace = this.loadingPlaces[0];
    const lastDeliveryPlace = this.deliveryPlaces[this.deliveryPlaces.length - 1];
    return (
      <Box sx={{ textAlign: 'center' }}>
        {(() => {
          if (!firstLoadingPlace) return <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.body }}>N/A</Typography>;
          const earliestLoading = (firstLoadingPlace as any).earliestLoadingDate;
          const latestLoading = (firstLoadingPlace as any).latestLoadingDate;
          const loadingDaysDiff = OfferRenderer.calculateDaysDifference(earliestLoading, latestLoading);
          const dateStr = OfferRenderer.formatDateShortJSX(earliestLoading);
          const start = OfferRenderer.formatTime(firstLoadingPlace.startTime);
          const end = OfferRenderer.formatTime(firstLoadingPlace.endTime);
          const timeStr = start && end ? ` ${start}-${end}` : start ? ` ${start}` : end ? ` ${end}` : '';
          const dateSuffix = loadingDaysDiff > 0 ? ` (+${loadingDaysDiff})` : '';
          return (
            <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.body }}>
              {dateStr}
              {dateSuffix && (
                <Box component="span" sx={{ color: 'text.green' }}>
                  {dateSuffix}
                </Box>
              )}
              {timeStr}
            </Typography>
          );
        })()}
        {(() => {
          if (!lastDeliveryPlace) return <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.small, color: 'text.grey' }}>N/A</Typography>;
          const earliestDelivery = (lastDeliveryPlace as any).earliestLoadingDate;
          const latestDelivery = (lastDeliveryPlace as any).latestLoadingDate;
          const deliveryDaysDiff = OfferRenderer.calculateDaysDifference(earliestDelivery, latestDelivery);
          const dateStr = OfferRenderer.formatDateShortJSX(earliestDelivery);
          const start = OfferRenderer.formatTime(lastDeliveryPlace.startTime);
          const end = OfferRenderer.formatTime(lastDeliveryPlace.endTime);
          const timeStr = start && end ? ` ${start}-${end}` : start ? ` ${start}` : end ? ` ${end}` : '';
          const dateSuffix = deliveryDaysDiff > 0 ? ` (+${deliveryDaysDiff})` : '';
          return (
            <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.small, color: 'text.grey' }}>
              {dateStr}
              {dateSuffix && (
                <Box component="span" sx={{ color: 'text.green' }}>
                  {dateSuffix}
                </Box>
              )}
              {timeStr}
            </Typography>
          );
        })()}
      </Box>
    );
  }

  renderPrices() {
    const displayPrice = getDisplayPrice(this.offer as PotentialOffer);
    const parts = displayPrice.type.split(' / ');
    const priceParts = displayPrice.price.split(' / ');
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.body, fontWeight: 400 }}>€</Typography>
          {priceParts.map((part, index) => (
            <React.Fragment key={index}>
              <Typography
                variant="body2"
                fontWeight={part === t('N/A') ? 200 : 600}
                sx={{
                  fontSize: (this.theme as any).typography.table.body,
                  color: index === priceParts.length - 1 && displayPrice.marginColor
                    ? (displayPrice.marginColor === 'green' ? 'text.successGreen' : 'text.red')
                    : 'text.dark'
                }}
              >
                {part}
              </Typography>
              {index < priceParts.length - 1 && (
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: (this.theme as any).typography.table.body, color: 'text.black' }}>
                  /
                </Typography>
              )}
            </React.Fragment>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              <Typography
                variant="caption"
                sx={{
                  color: index === parts.length - 1 && displayPrice.marginColor
                    ? (displayPrice.marginColor === 'green' ? '#4caf50' : '#f44336')
                    : '#666',
                  fontSize: (this.theme as any).typography.table.small,
                  fontWeight: index === parts.length - 1 && displayPrice.marginColor ? 600 : 400
                }}
              >
                {part}
              </Typography>
              {index < parts.length - 1 && (
                <Typography variant="caption" sx={{ color: 'text.grey', fontSize: (this.theme as any).typography.table.small }}>
                  /
                </Typography>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  }

  shouldDisplayScore(scoreKey: keyof OfferScoreComponents): boolean {
    if (!this.tenant || !this.tenant.scoringWeights) return false;
    return this.tenant.scoringWeights[scoreKey]?.value > 0;
  }

  /**
   * Normalize weight values to percentages that sum to 100% (same as backend logic)
   * Used for displaying the relative importance of each scoring factor
   */
  private normalizeWeightsToPercentage(weights: Record<string, any>): Record<string, number> {
    const enabledSum = Object.values(weights).reduce((acc, w) => acc + (w.value > 0 ? w.value : 0), 0);

    if (enabledSum === 0) return {};

    const normalized: Record<string, number> = {};
    Object.entries(weights).forEach(([key, weight]) => {
      normalized[key] = weight.value > 0 ? (weight.value / enabledSum) * 100 : 0;
    });

    return normalized;
  }

  renderScore() {
    const scoreLabels: Record<string, string> = {
      client_reliability: t('Client Score'),
      destination_score: t('Destination Score'),
      route_distance: t('Route Distance Score'),
      pickup_distance: t('Pickup Distance Score'),
      tonnage_efficiency: t('Tonnage Efficiency Score'),
      payment_terms: t('Payment Terms Score'),
      planning_feasibility: t('Planning Score'),
      operational_efficiency: t('Operational Score'),
      driver_compatibility: t('Driver Score'),
      risk_assessment: t('Risk Score'),
      negotiation_performance: t('Negotiation Score'),
      offer_freshness: t('Offer Freshness Score'),
      client_fair_price_score: t('Client price / fair price')
    };

    const scoreKeys = Object.keys(scoreLabels) as (keyof OfferScoreComponents)[];
    const weightPercentages = this.tenant?.scoringWeights ? this.normalizeWeightsToPercentage(this.tenant.scoringWeights) : {};

    return (
      <Box sx={{ textAlign: 'center' }}>
        {this.offer.computedScores?.final_score !== undefined && this.offer.computedScores.final_score > 0 ? (
          <Tooltip
            title={
              this.offer.computedScores ? (
                <Box sx={{ p: 1 }}>
                  {scoreKeys.map((key) => (
                    <React.Fragment key={key}>
                      {this.shouldDisplayScore(key) && this.offer.computedScores![key] !== undefined && (
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          {scoreLabels[key]}: {Math.round((this.offer.computedScores![key] as number) * 100)}
                          {weightPercentages[key] ? (
                            <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 300, ml: 0.5 }}>
                              ({Math.round(weightPercentages[key])}%)
                            </Box>
                          ) : null}
                        </Typography>
                      )}
                    </React.Fragment>
                  ))}
                  <Typography variant="caption" sx={{ display: 'block', pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.3)', fontWeight: 600 }}>
                    {t('Final Score')}: {Math.round(this.offer.computedScores.final_score * 100)}
                  </Typography>
                </Box>
              ) : 'Score breakdown not available'
            }
            arrow
          >
            <Stack direction="row" gap={0.5} justifyContent="center" alignItems="center" sx={{ cursor: 'help' }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem', lg: '0.9375rem' },
                  cursor: 'help'
                }}
              >
                {Math.round(this.offer.computedScores.final_score * 100)}
              </Typography>
              <Circle sx={{ color: OfferRenderer.getScoreColor(Math.round(this.offer.computedScores.final_score * 100)), fontSize: '0.75rem' }} />
            </Stack>
          </Tooltip>
        ) : (
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: (this.theme as any).typography.table.body }}>
            N/A
          </Typography>
        )}
      </Box>
    );
  }

  renderInfo() {
    return (
      <OfferRenderer.InfoTooltip offer={this.offer as PotentialOffer}>
        <IconButton
          size="small"
          sx={{
            p: 0.5,
            borderRadius: '50%',
          }}
        >
          <InfoOutlineIcon />
        </IconButton>
      </OfferRenderer.InfoTooltip>
    );
  }

  renderContactPerson() {
    const hasEmail = !!this.offer.contactPerson?.email;
    const hasMessenger = !!timocomPluginData(this.offer)?.useMessenger;
    const activeNego = this.offer.negotiations?.[0];
    const hasActiveNego = !!activeNego;
    const activeChannelIsChat = hasActiveNego && activeNego.useMessenger === true;
    const activeChannelIsEmail = hasActiveNego && !activeChannelIsChat;
    const fadedSx = { opacity: 0.25, filter: 'grayscale(1)' };
    const emailSx = hasActiveNego && !activeChannelIsEmail
      ? { fontSize: '1rem', color: 'text.secondary', ...fadedSx }
      : { fontSize: '1rem', color: activeChannelIsEmail ? 'primary.main' : 'text.secondary' };
    const chatSx = hasActiveNego && !activeChannelIsChat
      ? { fontSize: '1rem', color: 'info.main', ...fadedSx }
      : { fontSize: '1rem', color: activeChannelIsChat ? 'primary.main' : 'info.main' };
    const emailTooltip = activeChannelIsEmail ? t('Active channel: Email') : t('Email');
    const chatTooltip = activeChannelIsChat ? t('Active channel: Timocom Messenger') : 'Timocom Messenger';
    const companyName = this.offer.customer?.name;
    return (
      <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, overflow: 'hidden' }}>
        {companyName && (
          <Tooltip title={companyName} placement="top">
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{
                fontSize: (this.theme as any).typography.table.body,
                maxWidth: 'clamp(120px, 15vw, 220px)',
                cursor: 'help',
              }}
            >
              {companyName}
            </Typography>
          </Tooltip>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          {hasEmail && (
            <Tooltip title={emailTooltip} placement="top">
              <EmailOutlined sx={emailSx} />
            </Tooltip>
          )}
          {hasMessenger && (
            <Tooltip title={chatTooltip} placement="top">
              <ChatBubbleOutline sx={chatSx} />
            </Tooltip>
          )}
          {!hasEmail && !hasMessenger && (
            <Tooltip title={t('No available channel')} placement="top">
              <WarningAmberOutlined sx={{ fontSize: '1rem' }} color="warning" />
            </Tooltip>
          )}
          {this.offer.contactPerson?.email && (
            <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.small, color: 'text.secondary' }}>
              {this.offer.contactPerson.email}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  renderDistance() {
    const approachDistance = this.offer.computedPrices?.approachDistance || 0;
    const offerDistance = this.offer.computedPrices?.offerDistance || this.offer.distanceKms || 0;
    // If no approach distance, show just offer distance without tooltip
    if (!approachDistance) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem', lg: '0.9375rem' } }}>
            {Math.round(offerDistance)} km
          </Typography>
        </Box>
      );
    }
    // If approach distance is available, show with tooltip
    const totalDistance = approachDistance + offerDistance;
    return (
      <Tooltip
        title={
          <Box sx={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}>
            {`${t('Approach distance')}: ${Math.round(approachDistance)} km\n` +
              `${t('Offer distance')}: ${Math.round(offerDistance)} km\n` +
              `${t('Total distance')}: ${Math.round(totalDistance)} km`}
          </Box>
        }
        arrow
        placement="top"
      >
        <Box sx={{ textAlign: 'center', cursor: 'help' }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem', lg: '0.9375rem' } }}>
            {`${Math.round(approachDistance)} / ${Math.round(offerDistance)} km`}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.grey', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem', lg: '0.8125rem' } }}>
            {`${Math.round(totalDistance)} km`}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  renderWL() {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.body, color: this.offer.weightT && this.offer.weightT > 25 ? 'text.red' : 'inherit' }}>
          {this.offer.weightT ? `${this.offer.weightT} t` : t('N/A')}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.small, color: this.offer.lengthM && this.offer.lengthM > 13.60 ? 'text.red' : 'text.grey' }}>
          {this.offer.lengthM ? `${this.offer.lengthM} m` : t('N/A')}
        </Typography>
      </Box>
    );
  }

  renderStatus() {
    const showUnreadEmails = this.offer.unreadEmails !== undefined && this.offer.unreadEmails > 0;
    const showNeedAction = OfferRenderer.needAction(this.offer);
    const showWaitingForClient = !showNeedAction && this.offer.status === OfferStatus.NEGOTIATION_PRICE_RECEIVED;
    const hasIcons = showUnreadEmails || showNeedAction || showWaitingForClient;
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Tooltip title={OfferRenderer.getOfferStatusDescription(this.offer.status!)} arrow placement="top">
          <Chip
            size="small"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {OfferStatus[this.offer.status!]?.replace('NEGOTIATION_', '').replaceAll('_', ' ')}
              </Box>
            }
            sx={{ '& .MuiChip-label': { fontSize: '0.75rem'}}}
          />
        </Tooltip>
        {hasIcons && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
            {showUnreadEmails && (
              <Tooltip title={t('Unread Emails')}>
                <Badge badgeContent={this.offer.unreadEmails} color="error" sx={{ gap: 0.5 }}>
                  <Mail color="action" />
                </Badge>
              </Tooltip>
            )}
            {showNeedAction && (
              <Tooltip title={t('Requires Attention')}>
                <PsychologyAlt color="warning" sx={{ gap: 0.5 }} />
              </Tooltip>
            )}
            {showWaitingForClient && (
              <Tooltip title={t('Waiting for client’s response')}>
                <LockClock color="primary" sx={{ gap: 0.5 }} />
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    );
  }

  renderAge() {
    const creationDate = this.offer.creationDateTime || this.offer.createdAt;
    if (!creationDate) {
      return (
        <Typography variant="body2" sx={{ fontSize: (this.theme as any).typography.table.body, color: 'text.grey' }}>
          {t('N/A')}
        </Typography>
      );
    }

    const now = new Date();
    const created = new Date(creationDate);
    const diffMs = now.getTime() - created.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let label = '';
    let color = '';
    let bgColor = '';

    // Green spectrum: < 24 hours (fresher = lighter green, older = darker green)
    if (diffMinutes < 5) {
      label = '< 5min';
      color = '#ffffff';
      bgColor = this.theme.palette.success.light; // Lightest green
    } else if (diffMinutes < 10) {
      label = '< 10min';
      color = '#ffffff';
      bgColor = this.theme.palette.success.light;
    } else if (diffMinutes < 15) {
      label = '< 15min';
      color = '#ffffff';
      bgColor = this.theme.palette.success.main; // Medium light green
    } else if (diffMinutes < 30) {
      label = '< 30min';
      color = '#ffffff';
      bgColor = this.theme.palette.success.main;
    } else if (diffHours < 1) {
      label = '< 1h';
      color = '#ffffff';
      bgColor = this.theme.palette.success.main;
    } else if (diffHours < 2) {
      label = '< 2h';
      color = '#ffffff';
      bgColor = this.theme.palette.text.successGreen; // Medium green
    } else if (diffHours < 6) {
      label = '< 6h';
      color = '#ffffff';
      bgColor = this.theme.palette.text.successGreen;
    } else if (diffHours < 12) {
      label = '< 12h';
      color = '#ffffff';
      bgColor = this.theme.palette.success.dark; // Darker green
    } else if (diffDays < 1) {
      label = '< 1d';
      color = '#ffffff';
      bgColor = this.theme.palette.success.dark; // Darkest green
      // Orange spectrum: 1-2 days (fresher = lighter orange, older = darker orange)
    } else if (diffDays < 1.5) {
      label = '< 1.5d';
      color = '#ffffff';
      bgColor = this.theme.palette.warning.light; // Light orange
    } else if (diffDays < 2) {
      label = '< 2d';
      color = '#ffffff';
      bgColor = this.theme.palette.warning.main; // Medium orange
      // Red spectrum: 2+ days (fresher = lighter red, older = darker red)
    } else if (diffDays < 3) {
      label = `${diffDays}d`;
      color = '#ffffff';
      bgColor = this.theme.palette.error.light; // Light red
    } else if (diffDays < 7) {
      label = `${diffDays}d`;
      color = '#ffffff';
      bgColor = this.theme.palette.error.main; // Medium red
    } else {
      label = `${diffDays}d`;
      color = '#ffffff';
      bgColor = this.theme.palette.error.dark; // Dark red
    }

    return (
      <Tooltip
        title={
          <Box sx={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
            {`${t('Posted')}: ${created.toLocaleString()}`}
          </Box>
        }
        arrow
        placement="top"
      >
        <Chip
          label={label}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8125rem' },
            color: color,
            bgcolor: bgColor,
            border: 'none',
            borderRadius: '6px',
            cursor: 'help',
            px: 1,
            height: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
            },
          }}
        />
      </Tooltip>
    );
  }
}
